import { useQueryClient } from "@tanstack/react-query";
import { Swiper, SwiperSlide } from "components/Swiper";
import WhiteText from "components/WhiteText";
import {
  setEventFilters,
  setSelectedEvent,
  setSelectedSubscription,
} from "features/slices/reservationFlowSlice";
import { EventAvailabilityDetails, PlanPackagePromotionDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useCurrentUser from "hooks/useCurrentUser";
import React, { useMemo } from "react";
import { ImageBackground, Linking, Pressable, View } from "react-native";
import { get } from "services/http";
import {
  toAddNewPlanWithPlanPackageId,
  toScehdule,
  toSeatSelection,
  toUpgradePlan,
} from "utils/navigationUtils";
import { getUniqSubscriptions } from "utils/subscriptionUtils";

const PromotionAds = ({
  promotions,
}: {
  promotions: PlanPackagePromotionDetails[];
}) => {
  const { account } = useAccount();
  const { currentMember } = useCurrentUser();
  const dispatch = useAppDispatch();
  const { selectedSubscription } = useAppSelector(
    (state) => state.reservationFlow
  );
  const queryClient = useQueryClient();

  const toSeatSelectionPage = async (promotion: PlanPackagePromotionDetails) => {
    const subscriptionForPromotion = getUniqSubscriptions(
      account!.subscriptions
    ).find((sub) => sub.planPackage.id === promotion.planPackageId);

    const schedule = await queryClient.fetchQuery<EventAvailabilityDetails[]>(
      ["fetchEventAvailablities", promotion.planPackageId],
      () =>
        get({
          url: "/tickets/events",
          params: { accountId: account!.id, planPackageId: promotion.planPackageId },
        }) as Promise<EventAvailabilityDetails[]>
    );

    const selectedEvent = schedule?.find(
      (eventAvailablity: EventAvailabilityDetails) =>
        eventAvailablity.eventId === promotion.eventId
    );
    if (selectedEvent) {
      dispatch(setSelectedSubscription(subscriptionForPromotion!));
      dispatch(setSelectedEvent(selectedEvent));
      toSeatSelection();
    }
  };

  const handleImagePress = (promotion: PlanPackagePromotionDetails) => {
    switch (promotion.destination) {
      case "EXTERNAL_URL": {
        Linking.openURL(promotion.destinationUrl);
        break;
      }
      case "RESERVATION": {
        toSeatSelectionPage(promotion);
        break;
      }
      case "SCHEDULE": {
        const subscriptionAssigned = getUniqSubscriptions(
          account!.subscriptions
        ).find((sub) => sub.planPackage.id === promotion.planPackageId);

        dispatch(setSelectedSubscription(subscriptionAssigned!));
        dispatch(
          setEventFilters({
            months: promotion.months?.split(",") || [],
            days: promotion.days?.split(",") || [],
            eventTypes: promotion.eventTypes?.split(",") || [],
          })
        );
        toScehdule();
        break;
      }
      case "ADD_PLAN": {
        toAddNewPlanWithPlanPackageId(
          String(currentMember?.id),
          String(promotion.addNewPlanId)
        );
        break;
      }
      case "UPGRADE_PLAN": {
        toUpgradePlan(String(selectedSubscription?.id));
        break;
      }
      default: {
        break;
      }
    }
  };

  const activePromotions = useMemo(() => {
    if (!account || !promotions) return [];
    // Only display promotions that the user has a subscription to the plan package
    return promotions.filter((promotion) => {
      return account.subscriptions.some(
        (sub) => !sub.expired && sub.planPackage.id === promotion.planPackageId
      );
    });
  }, [promotions, account]);

  return (
    <View style={{ overflow: "hidden" }}>
      <Swiper
        style={{ width: "100%" }}
        loop={activePromotions.length > 1}
        autoplay={{
          delay: 4500,
          disableOnInteraction: false,
        }}
      >
        {activePromotions.map((promotion) => (
          <SwiperSlide key={promotion.id} style={{ width: "100%" }}>
            <Pressable onPress={() => handleImagePress(promotion)}>
              <ImageBackground
                source={{ uri: promotion.imageUrl }}
                style={{ aspectRatio: 21 / 9, position: "relative" }}
                imageStyle={{ borderRadius: 15 }}
              >
                {promotion.title && (
                  <View className="absolute bottom-0 h-14 p-2 w-full bg-black rounded-b-lg bg-opacity-50">
                    <WhiteText>{promotion.title}</WhiteText>
                  </View>
                )}
              </ImageBackground>
            </Pressable>
          </SwiperSlide>
        ))}
      </Swiper>
    </View>
  );
};

export default PromotionAds;
